import React, { ForwardedRef, forwardRef, lazy } from 'react'
import { Typography, Zoom } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { LocationData } from 'services/location'
import Suspense from 'components/Suspense'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { LocationFields_User$key } from '__generated__/LocationFields_User.graphql'
import { useFragment, graphql } from 'react-relay'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import { LocationFields_UpdateUserMutation } from '__generated__/LocationFields_UpdateUserMutation.graphql'
import Address from './components/Address'
import useStyles from './styles'

// Mapbox does not work on the server side
const FreelancerLocationMap = lazy(
  () => import(/* webpackChunkName: "FreelancerLocationMap" */'components/FreelancerLocationMap'),
)

interface ILocationFieldsProps {
  editable?: boolean
  user: LocationFields_User$key
}

function LocationFields(props: ILocationFieldsProps, ref: ForwardedRef<HTMLDivElement>) {
  const { editable, user: userFragmentRef } = props
  const { classes } = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { showSnackbarMessage } = useSnackbar()
  const user = useFragment(graphql`
    fragment LocationFields_User on User {
      profile {
        locationBounds0
        locationBounds1
        locationBounds2
        locationBounds3
        locationLatitude
        locationLongitude
        fullAddress
      }
    }
  `, userFragmentRef)

  const { execute, loading } = useQuickCommit<LocationFields_UpdateUserMutation>(graphql`
    mutation LocationFields_UpdateUserMutation($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          profile {
            locationBounds0
            locationBounds1
            locationBounds2
            locationBounds3
            locationLatitude
            locationLongitude
            fullAddress
          }
        }
      }
    }
  `)

  const locationStyle = {
    marginTop: 0,
    marginBottom: 0,
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderBottom: `2px solid ${theme.palette.secondary.main[50]}`,
    height: isSmallScreen ? 160 : 240,
    zIndex: 2, // allow clicking the map near the avatar as well
  }

  const handleAddressSelect = (e: React.ChangeEvent<HTMLInputElement>, address: LocationData) => {
    e.preventDefault()

    execute({
      input: {
        profile: {
          locationBounds0: address.bounds[0],
          locationBounds1: address.bounds[1],
          locationBounds2: address.bounds[2],
          locationBounds3: address.bounds[3],
          locationLatitude: address.latitude,
          locationLongitude: address.longitude,
        },
      },
    }).then(() => {
      showSnackbarMessage('Location updated', { level: 'success' })
    })
  }

  if (!user?.profile) {
    return null
  }

  const error = user?.profile?.locationBounds0 || !editable ? null : 'Location is required'

  return (
    <div className={classes.container} ref={ref}>
      <div className={classes.map}>
        <Suspense noSsr fallback={<div style={{ height: 240, backgroundColor: '#eee' }} />}>
          <FreelancerLocationMap
            locationBounds0={user.profile.locationBounds0 || 0}
            locationBounds1={user.profile.locationBounds1 || 0}
            locationBounds2={user.profile.locationBounds2 || 0}
            locationBounds3={user.profile.locationBounds3 || 0}
            locationLatitude={user.profile.locationLatitude || 0}
            locationLongitude={user.profile.locationLongitude || 0}
            style={locationStyle}
          />
        </Suspense>
      </div>

      {error && (
        <Typography variant="body2" className={classes.error} data-cy="profile-location-error">
          {error}
        </Typography>
      )}

      <Zoom in={editable} mountOnEnter unmountOnExit style={{ transitionDelay: '1000ms' }}>
        <Address
          loading={loading}
          initialValue={user.profile.fullAddress}
          label="Enter your location..."
          onSelectLocation={handleAddressSelect}
          editable={editable}
        />
      </Zoom>
    </div>
  )
}

export default React.memo(forwardRef(LocationFields))
